import React from 'react';
import styled from '@emotion/styled/macro';
import Tag from '@components/table/sub-components/tag';
import vars from '@variables';
import VerticalGraphScale from './vertical-scale';
import { GraphLabelAtom, H4Atom, PAtom } from '@atoms/text-atoms/text-atoms';
import { formatNumberWithCommas } from '@utils/utils';
import { VerticalBarGraphProps } from './vertical-bar-graph.types';

const getPercentageValue = (value: number, max: number) => (value / max) * 100;

const VerticalBarGraph: React.FC<VerticalBarGraphProps> = ({
  type /* Can be either 'numeric' or 'percentage'*/,
  title,
  target,
  pill,
  bars,
  altText = null,
  className,
}) => {
  let maxValue = 100;

  if (type === 'numeric') {
    /* Pull highest value from data (then used to calculate height %ages) */
    const allValues = bars
      .map(bar => bar.value)
      .filter(value => value !== null && value !== undefined);
    if (target && target.value) allValues.push(target.value);
    maxValue =
      Math.max(...allValues) *
      1.15; /* Adds some space at the top of the graph */
  }

  if (altText !== null) {
    return (
      <div className={className} data-testid="vertical-bar-graph">
        <div className="title-and-tag">
          {title.length ? (
            <H4Atom className="graph-title">{title}</H4Atom>
          ) : null}
          <PAtom className="alt-text">{altText}</PAtom>
        </div>
      </div>
    );
  } else {
    return (
      <div className={className} data-testid="vertical-bar-graph">
        <div className="title-and-tag">
          {title.length ? (
            <H4Atom className="graph-title">{title}</H4Atom>
          ) : null}
          {pill && pill === 'TRUE' ? (
            <Tag colour={vars.deltaRed}>not aligned *</Tag>
          ) : pill && pill === 'FALSE' ? (
            <Tag colour={vars.deltaGreen}>aligned *</Tag>
          ) : null}
        </div>

        <div className="graph-wrapper">
          {type ===
          'percentage' /* Should the percentage-based scale be displayed */ ? (
            <div className="x-scale">
              <GraphLabelAtom className="x-value">100</GraphLabelAtom>
              <GraphLabelAtom className="x-value">75</GraphLabelAtom>
              <GraphLabelAtom className="x-value">50</GraphLabelAtom>
              <GraphLabelAtom className="x-value">25</GraphLabelAtom>
              <GraphLabelAtom className="x-value">%</GraphLabelAtom>
            </div>
          ) : null}

          <div className="bars-wrapper">
            {bars.map(
              (
                { value, label = '', colour = 'rgb(102,102,102, 0.5)' },
                index
              ) => (
                <div className="bar" key={`${index}-${label.slice(0, 4)}`}>
                  <GraphLabelAtom className="bar-value">
                    {type === 'percentage'
                      ? `${value}%`
                      : `${formatNumberWithCommas(value)}`}
                  </GraphLabelAtom>
                  <VerticalGraphScale />
                  <div
                    className="bar-shape"
                    style={{
                      height: `${getPercentageValue(value, maxValue)}%`,
                      background: colour,
                    }}
                  ></div>
                  <GraphLabelAtom className="bar-label">{label}</GraphLabelAtom>
                </div>
              )
            )}{' '}
            {target && target.value && (
              <div
                className="target-line"
                data-testid="vertical-bar-graph-target"
                style={{
                  bottom: `${getPercentageValue(target.value, maxValue)}%`,
                }}
              />
            )}
            {target && target.value && (
              <div
                className="target-labels"
                style={{
                  bottom: `${getPercentageValue(target.value, maxValue)}%`,
                }}
              >
                <GraphLabelAtom>
                  {target.label ? target.label : target.value}
                </GraphLabelAtom>
                <GraphLabelAtom>
                  2025
                  <br />
                  Target
                </GraphLabelAtom>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
};

const StyledVerticalBarGraph = styled(VerticalBarGraph)`
  width: 25rem;
  .graph-title {
    width: 100%;
    line-height: 120%;
    color: ${vars.plasticsBlue};
    font-weight: ${vars.fontWeightMedium};
    ${vars.fontSize4};
    padding: 0 0 0.8rem 0;
  }
  .title-and-tag {
    height: 9rem;
    padding-bottom: 11rem;
  }
  .graph-wrapper {
    width: 100%;
    height: 17rem;
    display: flex;
    .x-scale {
      height: 100%;
      margin-right: ${vars.spacingXs};
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
    }
    .bars-wrapper {
      position: relative;
      display: flex;
      justify-content: flex-start;
      width: 100%;
      border-left: 1px black solid;
      border-bottom: 1px black solid;
      .bar {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 1.9rem;
        margin-left: ${vars.spacingM};
        background: ${vars.greyHavens};
        box-sizing: content-box;
        border-left: 1.1rem ${vars.greyHavens} solid;
        border-right: 1.1rem ${vars.greyHavens} solid;
        .bar-value {
          margin-top: -${vars.spacingM};
        }
        ${VerticalGraphScale} {
          position: absolute;
          z-index: 50;
          margin: -1px -1px 0 0;
        }
        .bar-shape {
          position: absolute;
          bottom: 0;
          width: 1.9rem;
        }
        .bar-label {
          position: absolute;
          bottom: -1.6rem;
        }
      }
    }
    .target-line {
      margin-bottom: -2px;
      position: absolute;
      width: 100%;
      border-bottom: red dashed 1px;
    }
    .target-labels {
      text-align: right;
      position: absolute;
      right: 0;
      ${GraphLabelAtom} {
        &:last-of-type {
          margin-top: 1rem;
          margin-bottom: -2.8rem;
        }
      }
    }
  }
`;

export default StyledVerticalBarGraph;
